import React, {useRef} from 'react';
import {
  Animated,
  Pressable,
  StyleSheet,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, type Palette} from '@/shared/ui/theme';

/**
 * Поверхности, из которых собран каждый экран.
 *
 * ## Панель прибора: рамка, полоса заголовка, содержимое
 *
 * Это `Card` дизайн-системы SamVision (`ui-kit/components/layout`): заливка
 * `surface-raised`, рамка `border-default`, радиус 3pt и НИ ОДНОЙ тени — панели
 * стыкуются друг с другом и с краями окна, как приборы в стойке. Заголовок, когда
 * он есть, — отдельная полоса на `surface-inset` с линией под ней и красной риской
 * слева: метка «это отдельный прибор в стойке».
 *
 * Что здесь больше нет: скруглений до 14pt, тени под каждой карточкой и подъёма на
 * наведение. Глубина — это линия, а всплывающее отличается от врезанного плотной
 * тенью, которую носят только меню и диалог.
 *
 * ## Why one Card and not twenty inline boxes
 *
 * Before the first pass each screen re-declared `{borderWidth, borderRadius,
 * backgroundColor, borderColor}` inline — twenty copies of the same four lines, which
 * is why the radius could never be changed in one place.
 */
export type CardProps = {
  children: React.ReactNode;
  /**
   * `flat` — панель внутри панели: без рамки-акцента. `elevated` — всплывающая
   * поверхность (`surface-overlay`): меню, диалог.
   */
  variant?: 'default' | 'flat' | 'elevated';
  /** Inner padding in grid steps. 0 removes it (for edge-to-edge rows). */
  padding?: number;
  /** Corner override — a row inside a group wants square corners. */
  radius?: number;
  /** Полоса заголовка панели. Со значком и подписью — как у Hikvision. */
  title?: string;
  /** Пояснение в той же полосе, мельче и тише. */
  subtitle?: string;
  icon?: IconName;
  /** Кнопки у правого края полосы заголовка. */
  actions?: React.ReactNode;
  /** Выделить панель акцентной рамкой — главный блок экрана. */
  highlighted?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

const styles = StyleSheet.create({
  card: {borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden'},
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    minHeight: 32,
    paddingRight: spacing(3),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  /* Акцентная риска у заголовка. */
  headerRule: {width: 3, alignSelf: 'stretch'},
  headerBody: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(2),
    paddingVertical: spacing(1) + 2,
    paddingLeft: spacing(2) + 1,
  },
  headerIcon: {width: 20, height: 20, alignItems: 'center', justifyContent: 'center'},
  /* Заголовок панели — служебный стиль: 12px полужирным, как шапка таблицы. */
  headerTitle: {fontSize: 12, lineHeight: 16, flexShrink: 1},
  headerActions: {flexDirection: 'row', alignItems: 'center', gap: spacing(1), marginLeft: 'auto'},
  bubble: {alignItems: 'center', justifyContent: 'center', borderWidth: StyleSheet.hairlineWidth},
});

/** The base surface colour a variant sits on. */
function surfaceOf(p: Palette, variant: NonNullable<CardProps['variant']>): string {
  return variant === 'elevated' ? p.elevated : p.card;
}

/** Полоса заголовка панели — общая для `Card` и тех, кто рисует панель сам. */
export function CardHeader({
  title,
  subtitle,
  icon,
  actions,
  style,
}: {
  title?: string;
  subtitle?: string;
  icon?: IconName;
  actions?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  return (
    <View
      style={[styles.header, {backgroundColor: p.inset, borderBottomColor: p.border}, style]}>
      <View style={[styles.headerRule, {backgroundColor: p.primary}]} />
      <View style={styles.headerBody}>
        {icon ? (
          <View style={styles.headerIcon}>
            <Icon name={icon} size={15} color={p.textSecondary} />
          </View>
        ) : null}
        {title ? (
          <AppText variant="subtitle" style={styles.headerTitle} numberOfLines={1}>
            {title}
          </AppText>
        ) : null}
        {subtitle ? (
          <AppText variant="micro" numberOfLines={1}>
            {subtitle}
          </AppText>
        ) : null}
      </View>
      {actions ? <View style={styles.headerActions}>{actions}</View> : null}
    </View>
  );
}

export function Card({
  children,
  variant = 'default',
  padding = 3,
  radius = radii.md,
  title,
  subtitle,
  icon,
  actions,
  highlighted = false,
  style,
  testID,
}: CardProps) {
  const p = useTheme();
  const hasHeader = !!title || !!actions;

  return (
    <View
      testID={testID}
      style={[
        styles.card,
        {
          borderRadius: radius,
          backgroundColor: surfaceOf(p, variant),
          borderColor: highlighted ? p.primary : variant === 'elevated' ? p.borderStrong : p.border,
        },
        !hasHeader && {padding: spacing(padding)},
        style,
      ]}>
      {hasHeader ? (
        <>
          <CardHeader title={title} subtitle={subtitle} icon={icon} actions={actions} />
          <View style={{padding: spacing(padding)}}>{children}</View>
        </>
      ) : (
        children
      )}
    </View>
  );
}

/**
 * A card you can press, with the scale-and-settle feedback a phone user expects and the
 * hover wash a pointer expects.
 *
 * Opacity alone is ambiguous on a light card — it reads as "this went disabled" as
 * easily as "you touched it". A 2% scale-down is unmistakably a press, runs on the
 * native driver, and costs nothing. Hover is `surface-hover`, not a lift: у
 * рабочей станции ничего не приподнимается, меняется заливка и рамка.
 */
export type PressCardProps = PressableProps & {
  children: React.ReactNode;
  variant?: CardProps['variant'];
  padding?: number;
  style?: StyleProp<ViewStyle>;
  /** Radius override — rows inside a group want square corners. */
  radius?: number;
};

export function PressCard({
  children,
  variant = 'default',
  padding = 3,
  style,
  radius = radii.md,
  ...rest
}: PressCardProps) {
  const p = useTheme();
  const scale = useRef(new Animated.Value(1)).current;
  const surface = surfaceOf(p, variant);

  const spring = (to: number) =>
    Animated.spring(scale, {
      toValue: to,
      useNativeDriver: true,
      speed: 40,
      bounciness: 0,
    }).start();

  return (
    <Animated.View style={{transform: [{scale}]}}>
      <Pressable
        accessibilityRole="button"
        {...rest}
        onPressIn={e => {
          spring(0.98);
          rest.onPressIn?.(e);
        }}
        onPressOut={e => {
          spring(1);
          rest.onPressOut?.(e);
        }}
        style={state => [
          styles.card,
          {
            borderRadius: radius,
            borderColor: isHot(state) ? p.borderStrong : p.border,
            backgroundColor: isHot(state) ? p.elevated : surface,
            padding: spacing(padding),
          },
          typeof style === 'function' ? style(state) : style,
        ]}>
        {children}
      </Pressable>
    </Animated.View>
  );
}

/** True while the pointer is over the control, or a finger is down on it. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * A plate behind an icon.
 *
 * Значок на подложке — объект, значок в строке — подсказка. Подложка — `surface-inset`
 * с тонкой рамкой `border-subtle` (так ui-kit рисует значок раздела и пустое
 * состояние), и почти прямая: радиус 3pt, а не круг. Круг остаётся у лиц.
 */
export function IconBubble({
  children,
  tone,
  size = 40,
  square = true,
  style,
}: {
  children: React.ReactNode;
  /** The plate colour — a status soft, or (by default) the inset grey. */
  tone?: string;
  size?: number;
  /** Круг — для людей; квадрат — для устройств, разделов и всего остального. */
  square?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  return (
    <View
      style={[
        styles.bubble,
        {
          width: size,
          height: size,
          borderRadius: square ? radii.md : size / 2,
          backgroundColor: tone ?? p.inset,
          borderColor: tone ? 'transparent' : p.borderSubtle,
        },
        style,
      ]}>
      {children}
    </View>
  );
}

/** A hairline divider for use INSIDE a card, inset past a leading avatar/icon. */
export function Divider({inset = 0, strong = false}: {inset?: number; strong?: boolean}) {
  const p = useTheme();
  return (
    <View
      style={{
        height: StyleSheet.hairlineWidth,
        backgroundColor: strong ? p.border : p.borderSubtle,
        marginLeft: inset,
      }}
    />
  );
}
